/**
 * API client for Gordon — the HQ reminder-engine cockpit. Backend: oh_procurement
 * Django app, endpoints under /oh_procurement/alfred/hq/reminder-controls*. The
 * engine is plugin-based, so test-send and audit take a `plugin` key and an
 * update names the `plugin` it changes. Every `apiEndpoints.X` reference lives
 * INSIDE a function body (TDZ-safety — mirrors interactionAudit.js), and every
 * fn returns `response.data` (oh-code admin-portal api-client convention).
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, post, put } from '../request'

// The ReminderControl singleton: { paused, plugins: [...], updated_at/by }.
const fetchReminderControls = async () => {
  try {
    const response = await get(apiEndpoints.procurementReminderControls)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching reminder controls')
    throw error
  }
}

// Update the master switch and/or one plugin. Partial body ok — only the sent
// keys change. A plugin update sends { plugin, enabled?, ...plugin knobs }.
const updateReminderControls = async (payload) => {
  try {
    const response = await put(apiEndpoints.procurementReminderControls, payload)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating reminder controls')
    throw error
  }
}

// DM the given plugin's current sample to one Slack user, tagged [TEST]. Blank
// recipient -> that plugin's configured test DM (default).
const sendReminderTest = async (plugin, recipientSlackId) => {
  try {
    const response = await post(apiEndpoints.procurementReminderTestSend, {
      plugin,
      recipient_slack_id: recipientSlackId || ''
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in sending reminder test message')
    throw error
  }
}

// One plugin's logic + the actual messages it sends (for the "How this works"
// popup): { how_it_works: [str], messages: [{ title, when, channel, text }] }.
const fetchReminderExplain = async (plugin, month) => {
  try {
    const response = await get(
      makeRequestUrl(apiEndpoints.procurementReminderExplain, { plugin, month })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching reminder logic')
    throw error
  }
}

// One plugin's send ledger, newest first: { results, count }.
const fetchReminderAudit = async ({ plugin, limit = 50, offset = 0 } = {}) => {
  try {
    const response = await get(
      makeRequestUrl(apiEndpoints.procurementReminderAudit, { plugin, limit, offset })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching reminder audit')
    throw error
  }
}

// What the procurement cycle would send on a given morning (default today):
// { date, order_month, deadline, blocked, override, gate_clear,
//   missing_budgets, messages: [{ audience, stage,
//   destination, is_override, text }] }. A forecast, not a log.
const fetchReminderPlan = async (planDate, plugin) => {
  try {
    const response = await get(
      makeRequestUrl(apiEndpoints.procurementReminderPlan, {
        date: planDate || '',
        plugin: plugin || ''
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching reminder plan')
    throw error
  }
}

// The whole order month, one entry per morning:
// { order_month, opens, deadline, ends, today, projection_note,
//   days: [{ date, actual, today, phase, total,
//            items: [{ stage, label, count, failed }] }] }.
// History comes from the ledger; days from today on are a projection.
const fetchReminderCycle = async (onDate, plugin) => {
  try {
    const response = await get(
      makeRequestUrl(apiEndpoints.procurementReminderCycle, {
        date: onDate || '',
        plugin: plugin || ''
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching reminder cycle')
    throw error
  }
}

// What has ALREADY been posted, newest first, each with the exact text and
// whether it can still be pulled back:
// { results: [{ uuid, centre, stage, send_date, destination, channel, text,
//               delivered, retracted, can_recall, recall_blocked_because }] }.
const fetchReminderSent = async ({ period = '', limit = 60, plugin = '' } = {}) => {
  try {
    const response = await get(
      makeRequestUrl(apiEndpoints.procurementReminderSent, { period, limit, plugin })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching sent reminders')
    throw error
  }
}

// Edit or delete messages that are already in Slack. `text` given -> every
// message named is rewritten to it; `text` omitted -> they are deleted.
// Returns { action, ok, failed, results: [{ uuid, label, ok, error }] } — one
// bad channel reports itself and does not stop the rest.
const recallReminders = async (uuids, text, plugin) => {
  try {
    const body = { uuids }
    if (text) body.text = text
    // Which stream's ledger these uuids belong to. Omitted -> Program Material,
    // the only stream when this endpoint was written.
    if (plugin) body.plugin = plugin
    const response = await post(apiEndpoints.procurementReminderRecall, body)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in recalling reminder messages')
    throw error
  }
}

export {
  fetchReminderControls,
  updateReminderControls,
  sendReminderTest,
  fetchReminderExplain,
  fetchReminderAudit,
  fetchReminderPlan,
  fetchReminderCycle,
  fetchReminderSent,
  recallReminders
}
